import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog-v2";
import { cn } from "~/lib/utils";

interface ModalProps {
  title: ReactNode;
  description: ReactNode;
  content?: ReactNode;
  footer?: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function Modal({
  title,
  description,
  content,
  footer,
  open,
  onOpenChange,
}: ModalProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setIsScrolled(scrollTop > 0);
    setIsScrolledToBottom(scrollTop + clientHeight < scrollHeight);
  }, []);

  const setScrollEl = useCallback(
    (node: HTMLDivElement | null) => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", handleScroll);
      }
      scrollRef.current = node;

      if (node) {
        node.addEventListener("scroll", handleScroll);
        requestAnimationFrame(handleScroll);
      }
    },
    [handleScroll]
  );

  useEffect(() => {
    return () => {
      if (scrollRef.current) {
        scrollRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {content && (
          <div
            ref={setScrollEl}
            className={cn(
              "flex-1 overflow-y-auto overflow-x-hidden px-6 pb-2 [&_p]:break-words [&_h4]:break-words",
              isScrolled && "border-t",
              isScrolledToBottom && "border-b"
            )}
          >
            {content}
          </div>
        )}

        {footer && (
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 justify-end">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
